import legalClearanceLevelValidator from 'forms/sections/legal/shared/clearance';
import { checkValueIncluded, hasYesOrNo } from 'models/validate';
import { US_DEPT_OF_TREASURY, FOREIGN_GOVT, OTHER } from 'constants/enums/legalOptions';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { getSafeDateTime, sortSafeDateTimes, setToStartOfMonth } from 'helpers/date';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { isPVQForm } from 'helpers/form';
import pvqInvestigatingAgency from 'forms/shared/validators/models/pvqInvestigatingAgency/pvqInvestigatingAgency';
import { LegalInvestigationsClearanceHistoryItemModel, LegalInvestigationsHistoryItemModel } from '../types';

export const legalInvestigationsHistoryItemValidator: ValidateJSModel<LegalInvestigationsHistoryItemModel> = {
  AgencyNotApplicable: {},
  ClearanceLevelNotApplicable: {},
  CompletedNotApplicable: {},
  GrantedNotApplicable: {},
  Agency: (value, attributes, attributeName, options) => {
    if (attributes?.AgencyNotApplicable?.applicable === false) {
      return {};
    }

    if (isPVQForm(options?.formType)) {
      return { presence: true, model: { validator: pvqInvestigatingAgency } };
    }

    return { presence: true, hasValue: true };
  },
  AgencyExplanation: (value, attributes, attributeName, options = {}) => {
    if (!isPVQForm(options?.formType) && checkValueIncluded(attributes?.Agency, [US_DEPT_OF_TREASURY, FOREIGN_GOVT, OTHER])) {
      return { presence: true, hasValue: true };
    }

    if (isPVQForm(options?.formType) && checkValueIncluded(attributes?.Agency, [OTHER])) {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  Completed: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (
      attributes?.CompletedNotApplicable
      && attributes?.CompletedNotApplicable.applicable === false
    ) {
      return {};
    }

    const { applicantBirthdate } = options;

    return {
      presence: true,
      date: { earliest: setToStartOfMonth(applicantBirthdate), latest: DEFAULT_LATEST, tooEarly: 'error.date.min' },
    };
  },
  Issued: {},
  Granted: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    const { applicantBirthdate } = options;

    if (attributes?.GrantedNotApplicable && attributes?.GrantedNotApplicable.applicable === false) {
      return {};
    }

    if (
      options?.requireLegalInvestigationClearanceGranted
      && attributes?.ClearanceGranted
      && attributes?.ClearanceGranted.value === 'No'
    ) {
      return {};
    }

    const sortedDates = sortSafeDateTimes([
      { ...getSafeDateTime(attributes?.Completed), field: 'Completed' },
      { ...getSafeDateTime(applicantBirthdate), field: 'applicantBirthdate' },
    ]);
    const earliestDate = sortedDates.length ? setToStartOfMonth(sortedDates[sortedDates.length - 1]) : null;

    return {
      presence: true,
      date: {
        earliest: earliestDate?.datetime,
        latest: DEFAULT_LATEST,
        tooEarly: earliestDate?.field === 'Completed' ? 'error.date.clearanceGrantedDateTooEarly' : 'error.date.min'
      },
    };
  },
  ClearanceGranted: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (options?.requireLegalInvestigationClearanceGranted) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  ClearanceLevel: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (
      attributes?.ClearanceLevelNotApplicable
      && attributes?.ClearanceLevelNotApplicable.applicable === false
    ) {
      return {};
    }

    if (
      options?.requireLegalInvestigationClearanceGranted
      && attributes?.ClearanceGranted
      && attributes?.ClearanceGranted.value === 'No'
    ) {
      return {};
    }

    return { presence: true, model: { validator: legalClearanceLevelValidator } };
  },
  CompletedComments: {},
  GrantedComments: {},
};

export const legalInvestigationsClearanceHistoryItemValidator: ValidateJSModel<LegalInvestigationsClearanceHistoryItemModel> = {
  AgencyNotApplicable: {},
  Agency: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { presence: true, model: { validator: pvqInvestigatingAgency } };
    }

    // At first glance you may think these 2 can be joined under the same conditional, but they should be separate. If not PVQ, we need to check the applicable value
    if (attributes?.AgencyNotApplicable?.applicable !== false) {
      return { presence: true, model: { validator: pvqInvestigatingAgency } };
    }

    return {};
  },
  AgencyExplanation: (value, attributes) => {
    if (checkValueIncluded(attributes?.Agency, [OTHER])) {
      return { presence: true, hasValue: true };
    }

    return {};
  }
};
